import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MapPinIcon, ServerIcon, RadioIcon, ActivityIcon, UsersIcon } from 'lucide-react';
import { HuaweiOrder } from '../lib/supabase';
interface SiteDetailDrawerProps {
  site: HuaweiOrder | null;
  isOpen: boolean;
  onClose: () => void;
}
export function SiteDetailDrawer({
  site,
  isOpen,
  onClose
}: SiteDetailDrawerProps) {
  if (!site) return null;
  const DetailItem = ({
    label,
    value
  }: {
    label: string;
    value: any;
  }) => <div className="flex flex-col py-2" data-id="element-450" data-mp="15sh5j7l2qiw0">
      <span className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-451" data-mp="rt80n42bjb7c">
        {label}
      </span>
      <span className="text-sm text-slate-200 font-medium" data-id="element-452" data-mp="vujj8pmryw61">
        {value || '-'}
      </span>
    </div>;
  return <AnimatePresence data-id="element-453" data-mp="3cj7696vw586s">
      {isOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40" data-id="element-454" data-mp="3gksicq0lpifx" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25
      }} className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-slate-900 border-l border-slate-800 z-50 flex flex-col" data-id="element-455" data-mp="29u8l1uejsdvv">
            {/* Header */}
            <div className="p-5 border-b border-slate-800 flex justify-between items-center" data-id="element-456" data-mp="2srjgt50z0p6k">
              <div data-id="element-457" data-mp="37fxmdnprurqt">
                <h2 className="text-lg font-bold text-slate-100" data-id="element-458" data-mp="37ecs7z7ddath">
                  {site.Site_ID_NE}
                </h2>
                <p className="text-xs text-slate-400" data-id="element-459" data-mp="32inzaepuus65">
                  {site.Site_Name_NE}
                </p>
              </div>

              <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded" data-id="element-460" data-mp="3bhj69t0q46ai">
                <XIcon size={18} data-id="element-461" data-mp="294wfvq0hclbo" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6" data-id="element-462" data-mp="2e3pr1bsao08o">

              {/* MITRA 🔥 */}
              <section data-id="element-463" data-mp="2i3q5gwfe8bfq">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-464" data-mp="2i388tyotjv7a">
                  <UsersIcon size={16} data-id="element-465" data-mp="2mw1dyno795jw" />
                  <span className="text-xs uppercase" data-id="element-466" data-mp="2rubd19d1oqas">Mitra / Vendor</span>
                </div>

                <div className="bg-slate-950/50 p-3 rounded-lg" data-id="element-467" data-mp="2rwp99ljgo8jx">
                  <DetailItem label="Mitra" value={site.Mitra} data-id="element-468" data-mp="2wrura99qt32q" />
                </div>
              </section>

              {/* LOCATION */}
              <section data-id="element-469" data-mp="35u3x4lu2g89s">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-470" data-mp="35udr0ki8ezkr">
                  <MapPinIcon size={16} data-id="element-471" data-mp="17er89ubqcy0z" />
                  <span className="text-xs uppercase" data-id="element-472" data-mp="17fsvex60taz2">Location</span>
                </div>

                <div className="grid grid-cols-2 gap-3 bg-slate-950/50 p-3 rounded-lg" data-id="element-473" data-mp="1bh4cufeqqpw6">
                  <DetailItem label="TREG" value={site.TREG} data-id="element-474" data-mp="13wtqgc5nrumr" />
                  <DetailItem label="Regional" value={site.Regional_Tsel} data-id="element-475" data-mp="z5twnfru5soh" />
                  <DetailItem label="Area" value={site.Area} data-id="element-476" data-mp="z4ipr733g4t5" />
                </div>
              </section>

              {/* TECHNICAL */}
              <section data-id="element-477" data-mp="q5f5hzs6xjd2">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-478" data-mp="q66s4l3t86r7">
                  <RadioIcon size={16} data-id="element-479" data-mp="1mvxxxghjxgcb" />
                  <span className="text-xs uppercase" data-id="element-480" data-mp="1muwdilenbhhx">Technical</span>
                </div>

                <div className="grid grid-cols-2 gap-3 bg-slate-950/50 p-3 rounded-lg" data-id="element-481" data-mp="1hxmm507p4vrg">
                  <DetailItem label="Site Type" value={site.Tipe_Site} data-id="element-482" data-mp="1dtom8najrgxc" />
                  <DetailItem label="Frequency" value={site.Frekuensi} data-id="element-483" data-mp="18w6doy0fz3pv" />
                  <DetailItem label="Freq Group" value={site.Freq_Grouping} data-id="element-484" data-mp="18xzjyc757lxc" />
                  <DetailItem label="Capacity" value={site.Kapasitas} data-id="element-485" data-mp="2gw1hwg6bsqs" />
                </div>
              </section>

              {/* EQUIPMENT */}
              <section data-id="element-486" data-mp="1lcnxixr54lza">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-487" data-mp="1lce7ja63ietl">
                  <ServerIcon size={16} data-id="element-488" data-mp="1q4601q8taumd" />
                  <span className="text-xs uppercase" data-id="element-489" data-mp="1q57d209e1r6q">Equipment</span>
                </div>

                <div className="bg-slate-950/50 p-3 rounded-lg space-y-4" data-id="element-490" data-mp="1ce2ktj8co16f">

                  {/* ODU */}
                  <div data-id="element-491" data-mp="1gecnalwxo9gj">
                    <p className="text-xs text-slate-500 mb-2" data-id="element-492" data-mp="243ntwk0ysy2l">ODU</p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-493" data-mp="244gqwta4g97j">
                      <DetailItem label="NE Type" value={site.Tipe_ODU_NE} data-id="element-494" data-mp="2930h55vr075p" />
                      <DetailItem label="FE Type" value={site.Tipe_ODU_FE} data-id="element-495" data-mp="1v3953aosl0uc" />
                    </div>
                  </div>

                  {/* IDU */}
                  <div data-id="element-496" data-mp="200908qk3sj6o">
                    <p className="text-xs text-slate-500 mb-2" data-id="element-497" data-mp="203ww3wd0thok">IDU</p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-498" data-mp="jm95rv411lqt">
                      <DetailItem label="NE Type" value={site.Tipe_IDU_NE} data-id="element-499" data-mp="on6w4uz6xm3e" />
                      <DetailItem label="FE Type" value={site.Tipe_IDU_FE} data-id="element-500" data-mp="g5wkg83tybd3" />
                    </div>
                  </div>

                  {/* MODEM */}
                  <div data-id="element-501" data-mp="c3ro31lzz0mr">
                    <p className="text-xs text-slate-500 mb-2" data-id="element-502" data-mp="c6nn6t9kzvcs">Modem</p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-503" data-mp="q3kf6y1friv9">
                      <DetailItem label="NE" value={site.Modem_IDU_NE} data-id="element-504" data-mp="l4hfzu8506v7" />
                      <DetailItem label="FE" value={site.Modem_IDU_FE} data-id="element-505" data-mp="3tkssr5p4rwk6" />
                    </div>
                  </div>

                  {/* ANTENNA */}
                  <div data-id="element-506" data-mp="3onat65x06tou">
                    <p className="text-xs text-slate-500 mb-2" data-id="element-507" data-mp="3ojn0f8zdubb6">Antenna</p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-508" data-mp="76sx2rxmr9ss">
                      <DetailItem label="NE Size" value={site.Ukuran_Antenna_NE} data-id="element-509" data-mp="2dzr60qfdvf4" />
                      <DetailItem label="FE Size" value={site.Ukuran_Antenna_FE} data-id="element-510" data-mp="3aubbpydnupen" />
                    </div>
                  </div>
                </div>
              </section>

              {/* STATUS */}
              <section data-id="element-511" data-mp="19sbu2vq97iah">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-512" data-mp="19r0s91gdckm0">
                  <ActivityIcon size={16} data-id="element-513" data-mp="1eqwuav5ia1yx" />
                  <span className="text-xs uppercase" data-id="element-514" data-mp="1eplv78n09ie5">Status</span>
                </div>

                <div className="grid grid-cols-2 gap-3 bg-slate-950/50 p-3 rounded-lg" data-id="element-515" data-mp="1jj6h42bbqlwz">
                  <DetailItem label="Redeploy" value={site.Status_Redeploy} data-id="element-516" data-mp="1nifd7dtu6vvp" />
                  <DetailItem label="Material" value={site.Status_Material} data-id="element-517" data-mp="qtr79fveaz2n" />
                  <DetailItem label="NMS" value={site.Status_koneksi_NMS} data-id="element-518" data-mp="vsavxo1dioyp" />
                  <DetailItem label="Potensi" value={site.Status_Potensi_redeploy} data-id="element-519" data-mp="vskjz62uym1r" />
                </div>
              </section>

            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}